/**
 * General Sales Report (CWS-1230).
 *
 * Scope note: C-Store/Vending revenue (FR-07), comps (FR-19) and fleet/house
 * accounts with A/R aging (FR-21, FR-22) were removed from scope by product.
 * Voids and refunds (FR-20) carry counts and amounts only — no reason or
 * employee breakdown.
 *
 * `available: false` marks a metric whose supporting data is missing. Per PRD
 * §26 those render as "unavailable", never as zero.
 */

export type GeneralSalesRevenueCategory =
  'washes' | 'extras' | 'memberships' | 'prepaid_books' | 'other' | 'fees';

export type GeneralSalesCustomerType = 'retail' | 'membership' | 'prepaid';

export type GeneralSalesAdjustmentCategory = 'voids' | 'refunds';

export interface GeneralSalesKpi {
  key: string;
  label: string;
  /** Preformatted for display so the template never re-derives a metric. */
  value: string;
  /** Percentage change vs the previous equivalent period; null when not meaningful. */
  deltaPct: number | null;
  /** Absolute difference vs the previous period (PRD §20). Shown when a percentage is undefined. */
  deltaAbs?: number | null;
  /** Whether a rise is good. Churn and leakage invert the colour of the delta. */
  higherIsBetter: boolean;
  available: boolean;
  /** Shown under the value, e.g. the divisor used. */
  hint?: string;
}

export interface GeneralSalesTrendPoint {
  /** Bucket start date in the location's configured business timezone. */
  date: string;
  gross: number;
  net: number;
  /** Wash volume for the bucket, carrying the Wash Volume trend (PRD §19). */
  cars: number;
  carsPerDay: number;
  /** Recomputed per bucket from its own totals, never averaged from daily averages. */
  averageTicket: number | null;
  /** Aligned values from the comparison period; null when comparison is off. */
  previousGross: number | null;
  previousNet?: number | null;
}

/** How the trend series is bucketed for the selected window (PRD §19). */
export type GeneralSalesTrendGranularity = 'day' | 'week' | 'month';

export interface GeneralSalesRevenueRow {
  category: GeneralSalesRevenueCategory;
  label: string;
  units: number;
  revenue: number;
  sharePct: number;
}

export interface GeneralSalesVolume {
  totalCarsWashed: number;
  /** Paid orders of any type in the period: one per purchase, not per car. */
  totalPackagesPurchased: number;
  /** Total cars washed / calendar days in the selected period. */
  avgCarsPerDay: number;
  /** List price of every paid order (any type) plus card convenience fees. */
  grossSales: number;
  /**
   * Final price of every paid order plus card convenience fees, with no refund
   * deduction — the Car Wash Dashboard's Revenue figure (CWS-1368).
   */
  netSales: number;
  /** Gross minus net: discounts. Refunds are reported under leakage instead. */
  grossToNetGap: number;
  /** Null when there are no qualifying washes — renders as N/A, never 0. */
  averageTicket: number | null;
  /** The dashboard's cash / card split, on the settled payment's method. */
  cashCollected: number;
  cardCollected: number;
  trendGranularity: GeneralSalesTrendGranularity;
  trend: GeneralSalesTrendPoint[];
  revenueBreakdown: GeneralSalesRevenueRow[];
}

export interface GeneralSalesPackageRow {
  packageId: string;
  packageName: string;
  units: number;
  revenue: number;
  washSharePct: number;
  revenueSharePct: number;
  /** A genuine data gap: a wash with no package and no prepaid book (PRD §30.3). */
  unmapped: boolean;
  /** A wash redeemed from a prepaid book. Keyed by purchase, never by package. */
  prepaid: boolean;
}

export interface GeneralSalesExtraRow {
  addonId: string;
  name: string;
  units: number;
  revenue: number;
  attachRatePct: number;
  revenuePerAttachedCar: number;
}

export interface GeneralSalesExtras {
  /** Washes with at least one extra / eligible washes. Null when no eligible washes. */
  attachRatePct: number | null;
  /** Extra revenue / washes with an extra — never divided by all washes. */
  revenuePerAttachedCar: number | null;
  totalExtrasRevenue: number;
  washesWithExtra: number;
  eligibleWashes: number;
  rows: GeneralSalesExtraRow[];
}

export interface GeneralSalesCustomerMixRow {
  type: GeneralSalesCustomerType;
  label: string;
  washes: number;
  washSharePct: number;
  revenue: number;
  revenueSharePct: number;
}

/** Answers §10's question: is the business leaning more on membership traffic? */
export interface GeneralSalesCustomerMixSummary {
  membershipWashSharePct: number;
  membershipRevenueSharePct: number;
  /** Change in membership's share of washes, in percentage points. Null without a comparison. */
  washShareDeltaPts: number | null;
  membershipWorthLessPerWash: boolean;
}

export interface GeneralSalesMembershipBase {
  beginning: number;
  newSignups: number;
  /** Memberships that stopped in the period, cancelled or lapsed. */
  cancellations: number;
  lapsed: number; // subset of cancellations: ended with no cancellation recorded
  netChange: number;
  /** Memberships live at the end of the period. */
  ending: number;
  endingCustomers: number; // distinct customers live at period end (plan breakdown Total row)
}

export interface GeneralSalesMembershipTrendPoint {
  date: string;
  activeMembers: number;
}

export interface GeneralSalesMembershipFlowPoint {
  label: string;
  newSignups: number;
  cancellations: number;
}

/** One redeemed member wash with the subscription revenue allocated to it. */
export interface GeneralSalesMemberWashDetail {
  washId: string;
  orderRef: string;
  washedAt: string;
  packageName: string;
  subscriptionWashCount: number;
  allocatedRevenue: number;
}

/** One row of the Plan Performance table: live members on a single plan. */
export interface GeneralSalesMembershipPlanRow {
  packageId: string | null;
  planName: string;
  activeMembers: number;
  /** One customer on two plans counts once here, as on the membership cards. */
  activeCustomers: number;
  /** Share of base.ending — not of the rows' own total, so drift stays visible. */
  sharePct: number | null;
}

export interface GeneralSalesMembership {
  available: boolean;
  /** subscriptions carry no location_id, so the base counts ignore a location filter. */
  baseIsTenantWide?: boolean;
  base: GeneralSalesMembershipBase;
  /** Ending members split by plan; sums to base.ending by construction. */
  byPlan?: GeneralSalesMembershipPlanRow[];
  avgActiveMembers: number;
  churnPct: number | null;
  /** Membership revenue billed / average active members. */
  arpm: number | null;
  avgWashesPerMember: number | null;
  /** Days the washes-per-member figure covers, so it is never mislabelled 'per month'. */
  avgWashesPerMemberPeriodDays?: number;
  captureRatePct: number | null;
  eligibleRetailCustomers: number;
  /** Billed and redeemed are separate concepts and are never combined (PRD §11.3). */
  revenueBilled: number;
  /** Plans sold in the period. Counted, not summed: a comped plan bills $0 and still sold. */
  planSaleCount?: number;
  planSaleRevenue?: number;
  /** False when members exist but no billing is recorded — revenue is unknown, not zero. */
  billingAvailable?: boolean;
  washesRedeemed: number;
  /** Distinct customers who started a plan — the capture-rate numerator. */
  newSignupCustomers?: number;
  /** Subscription revenue spread across the washes it was redeemed against. */
  allocatedRevenue?: number;
  /** Billed to subscriptions that redeemed no wash this period; surfaced, not discarded. */
  unallocatedRevenue?: number;
  revenuePerMemberWash?: number | null;
  washDetailLimit?: number;
  washDetail?: GeneralSalesMemberWashDetail[];
  trend: GeneralSalesMembershipTrendPoint[];
  flow: GeneralSalesMembershipFlowPoint[];
}

export interface GeneralSalesDiscountRow {
  type: string;
  count: number;
  amount: number;
  pctOfGross: number;
}

export interface GeneralSalesRewashReasonRow {
  /** Display label — 'Soap residue left on vehicle'. */
  reason: string;
  /** The slug stored in `orders.rewash_reason`, which the filter matches on. */
  reasonKey: string;
  count: number;
  revenueImpact: number;
  pctOfRewashes: number;
}

export interface GeneralSalesRewashEmployeeRow {
  employeeId: string;
  employeeName: string;
  count: number;
  revenueImpact: number;
  pctOfRewashes: number;
}

export interface GeneralSalesAdjustmentRow {
  category: GeneralSalesAdjustmentCategory;
  label: string;
  count: number;
  amount: number;
  pctOfGross: number;
}

/** One prepaid wash book's activity in the period. */
export interface GeneralSalesPrepaidBookRow {
  bookId: string;
  bookName: string;
  booksSold: number;
  /** Washes those books carry — one book holds many. */
  washesPurchased: number;
  revenue: number;
  washesRedeemed: number;
}

/**
 * Prepaid wash books. Books sold and washes redeemed are different cohorts — a
 * wash redeemed now usually came from a book sold earlier — so the two are
 * reported side by side and never divided into a rate.
 */
export interface GeneralSalesPrepaid {
  booksSold: number;
  /** Washes the books sold this period carry — one book holds many. */
  washesPurchased: number;
  /** Money taken for washes not yet delivered: deferred, not wash revenue. */
  revenue: number;
  washesRedeemed: number;
  rows: GeneralSalesPrepaidBookRow[];
}

/** One cancelled order behind the Voids count, with why it was called off. */
export interface GeneralSalesCancelledOrderRow {
  orderId: string;
  orderCode: string | null;
  /** Appointment date, in the report's business timezone. */
  date: string | null;
  cancelledAt: string | null;
  customerName: string | null;
  licensePlate: string | null;
  washType: string;
  packageName: string | null;
  amount: number;
  cancelledBy: string | null;
  reason: string | null;
  refundStatus: string | null;
}

export interface GeneralSalesLeakage {
  discounts: GeneralSalesDiscountRow[];
  discountTotalAmount: number;
  discountTotalCount: number;
  discountPctOfGross: number;
  rewashesByReason: GeneralSalesRewashReasonRow[];
  rewashesByEmployee: GeneralSalesRewashEmployeeRow[];
  rewashTotalCount: number;
  rewashTotalImpact: number;
  adjustments: GeneralSalesAdjustmentRow[];
  adjustmentTotalCount: number;
  adjustmentTotalAmount: number;
  adjustmentPctOfGross: number;
  /** The orders behind the Voids count. Capped; see cancelledOrdersTruncated. */
  cancelledOrders: GeneralSalesCancelledOrderRow[];
  cancelledOrdersTruncated: boolean;
  /** Discounts + rewash impact + voids + refunds. */
  totalLeakage: number;
  totalLeakagePctOfGross: number;
}

export interface GeneralSalesReportMeta {
  startDate: string;
  endDate: string;
  calendarDays: number;
  locationLabel: string;
  locationCount: number;
  timezone: string;
  /** 'location' when a single location's configured zone was used, else 'requested'. */
  timezoneSource?: 'location' | 'requested';
  compareEnabled: boolean;
  compareStartDate: string | null;
  compareEndDate: string | null;
  /** Echo of the filters the server applied. */
  appliedFilters?: Record<string, unknown>;
  /** Filter keys that narrow only part of the report. */
  sectionScopedFilters?: string[];
}

export interface GeneralSalesPermissions {
  /** Gates the rewash-by-employee table. Must also be enforced server-side. */
  canViewEmployeeLeakage: boolean;
}

export interface GeneralSalesReportData {
  meta: GeneralSalesReportMeta;
  kpis: GeneralSalesKpi[];
  volume: GeneralSalesVolume;
  packageMix: GeneralSalesPackageRow[];
  packageMixTotals: {
    units: number;
    revenue: number;
  };
  extras: GeneralSalesExtras;
  customerMix: GeneralSalesCustomerMixRow[];
  customerMixSummary?: GeneralSalesCustomerMixSummary;
  membership: GeneralSalesMembership;
  prepaid: GeneralSalesPrepaid;
  leakage: GeneralSalesLeakage;
  permissions: GeneralSalesPermissions;
}

/** One selectable value in a report filter control. */
export interface GeneralSalesFilterOption {
  id: string;
  name: string;
}

/**
 * Options for the filter controls, derived server-side from what the tenant
 * actually has in the selected window — a control never offers a dead value.
 */
export interface GeneralSalesFilterOptions {
  packages: GeneralSalesFilterOption[];
  paymentMethods: GeneralSalesFilterOption[];
  discountTypes: GeneralSalesFilterOption[];
  rewashReasons: GeneralSalesFilterOption[];
  rewashEmployees: GeneralSalesFilterOption[];
  customerTypes: GeneralSalesFilterOption[];
}

export type GeneralSalesCustomerTypeFilter = 'all' | 'retail' | 'membership';

/**
 * Report filters (PRD §5).
 *
 * `rewash_reasons` and `employee_ids` narrow the rewash tables ONLY — filtering
 * gross sales by a rewash reason would be meaningless, and `orders` carries no
 * employee at all. Everything else narrows every order-derived section.
 */
export interface GeneralSalesFilters {
  package_ids: string[];
  customer_type: GeneralSalesCustomerTypeFilter;
  payment_methods: string[];
  discount_types: string[];
  rewash_reasons: string[];
  employee_ids: string[];
}

export interface GeneralSalesFilterOptionsRequest {
  start_date: string;
  end_date: string;
  location_ids?: string[];
  timezone?: string;
}

export function emptyGeneralSalesFilters(): GeneralSalesFilters {
  return {
    package_ids: [],
    customer_type: 'all',
    payment_methods: [],
    discount_types: [],
    rewash_reasons: [],
    employee_ids: [],
  };
}

export interface GeneralSalesReportRequest {
  start_date: string;
  end_date: string;
  location_ids?: string[];
  /** IANA zone. A single-location report is grouped in that location's configured zone instead. */
  timezone?: string;
  compare?: boolean;
  filters?: GeneralSalesFilters;
  /** Return the cancelled-order list behind the Voids count. Server default: false. */
  include_cancelled_orders?: boolean;
}
